import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LineChart, Line } from "recharts";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppFab from "@/components/WhatsAppFab";
import { api, formatNum, formatApiError } from "@/lib/api";
import { ArrowsLeftRight, TrendUp, TrendDown, Minus } from "@phosphor-icons/react";

export default function HistoricalCompare() {
    const [analyses, setAnalyses] = useState([]);
    const [idA, setIdA] = useState("");
    const [idB, setIdB] = useState("");
    const [comparison, setComparison] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        async function load() {
            try {
                const { data } = await api.get("/analyses");
                setAnalyses(data.items || []);
                // Auto-seleccionar los 2 más recientes de la misma empresa
                const items = data.items || [];
                if (items.length >= 2) {
                    const firstCompany = items[0].company_name;
                    const sameCompany = items.filter((i) => i.company_name === firstCompany);
                    if (sameCompany.length >= 2) {
                        setIdA(sameCompany[0].id);
                        setIdB(sameCompany[1].id);
                    } else {
                        setIdA(items[0].id);
                        setIdB(items[1].id);
                    }
                }
            } catch (e) {
                setError(formatApiError(e));
            }
        }
        load();
    }, []);

    async function compare() {
        if (!idA || !idB || idA === idB) {
            setError("Selecciona dos análisis diferentes");
            return;
        }
        setError("");
        setLoading(true);
        try {
            const { data } = await api.get(`/analyses/${idA}/compare/${idB}`);
            setComparison(data);
        } catch (e) {
            setError(formatApiError(e, "No se pudo comparar"));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (idA && idB && idA !== idB) compare();
    }, [idA, idB]);  // eslint-disable-line

    const chartData = useMemo(() => {
        if (!comparison) return [];
        return Object.entries(comparison.comparison).map(([k, v]) => ({
            ratio: v.label.replace(/\s/g, "\n"),
            [comparison.a.period]: v.a || 0,
            [comparison.b.period]: v.b || 0,
        }));
    }, [comparison]);

    return (
        <div className="min-h-screen flex flex-col">
            <Navbar />
            <main className="flex-1 max-w-7xl mx-auto w-full px-6 md:px-12 py-12">
                <div className="mb-10 pb-8 border-b border-[var(--gf-border)]">
                    <div className="flex items-center gap-2 mb-3">
                        <ArrowsLeftRight size={20} weight="light" className="text-[var(--gf-gold)]" />
                        <span className="micro-label">— Comparativa histórica</span>
                    </div>
                    <h1 className="serif text-4xl md:text-5xl">Evolución entre ejercicios</h1>
                    <p className="text-[var(--gf-text-secondary)] mt-2">
                        Selecciona dos análisis de tu historial y compara cómo evolucionan los ratios financieros.
                    </p>
                </div>

                {error && (
                    <div className="text-sm text-[var(--gf-danger)] border-l-2 border-[var(--gf-danger)] pl-3 py-2 bg-red-50 mb-6">
                        {error}
                    </div>
                )}

                {analyses.length < 2 ? (
                    <div className="gf-card text-center py-14" data-testid="compare-insufficient">
                        <p className="text-[var(--gf-text-secondary)] mb-6">
                            Necesitas al menos 2 análisis en tu historial para comparar.
                            Actualmente tienes {analyses.length}.
                        </p>
                        <Link to="/analisis/nuevo" className="btn-gold inline-flex">
                            Crear nuevo análisis
                        </Link>
                    </div>
                ) : (
                    <>
                        <div className="gf-card-ficha mb-8">
                            <div className="grid md:grid-cols-[1fr_auto_1fr] gap-6 items-center">
                                <div>
                                    <label className="gf-label">Análisis A</label>
                                    <select className="gf-input mono" value={idA} onChange={(e) => setIdA(e.target.value)} data-testid="select-a">
                                        <option value="">— Selecciona —</option>
                                        {analyses.map((a) => (
                                            <option key={a.id} value={a.id}>
                                                {a.company_name} · {a.period} ({new Date(a.created_at).toLocaleDateString("es-ES")})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <ArrowsLeftRight size={28} className="text-[var(--gf-gold)]" weight="light" />
                                <div>
                                    <label className="gf-label">Análisis B</label>
                                    <select className="gf-input mono" value={idB} onChange={(e) => setIdB(e.target.value)} data-testid="select-b">
                                        <option value="">— Selecciona —</option>
                                        {analyses.map((a) => (
                                            <option key={a.id} value={a.id}>
                                                {a.company_name} · {a.period} ({new Date(a.created_at).toLocaleDateString("es-ES")})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {loading ? (
                            <div className="text-center py-10"><span className="elegant-loader"><span /><span /><span /></span></div>
                        ) : comparison && (
                            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="comparison-result">
                                <div className="grid md:grid-cols-2 gap-6 mb-10">
                                    <PeriodHeader analysis={comparison.a} color="var(--gf-navy)" />
                                    <PeriodHeader analysis={comparison.b} color="var(--gf-gold)" />
                                </div>

                                {/* Gráfica */}
                                <div className="gf-card mb-10">
                                    <div className="micro-label mb-4">— Evolución de ratios</div>
                                    <ResponsiveContainer width="100%" height={380}>
                                        <BarChart data={chartData} margin={{ top: 10, right: 20, bottom: 80 }}>
                                            <CartesianGrid strokeDasharray="2 2" stroke="#E5E0D8" />
                                            <XAxis dataKey="ratio" tick={{ fontSize: 10 }} angle={-35} textAnchor="end" interval={0} height={100} stroke="#8B95A5" />
                                            <YAxis tick={{ fontSize: 11 }} stroke="#8B95A5" />
                                            <Tooltip />
                                            <Legend />
                                            <Bar dataKey={comparison.a.period} fill="#0B192C" />
                                            <Bar dataKey={comparison.b.period} fill="#C5A880" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>

                                {/* Tabla detalle */}
                                <div className="gf-card">
                                    <div className="micro-label mb-4">— Detalle ratio a ratio</div>
                                    <div className="overflow-x-auto">
                                        <table className="data-table">
                                            <thead>
                                                <tr>
                                                    <th>Ratio</th>
                                                    <th className="text-right">{comparison.a.period}</th>
                                                    <th className="text-right">{comparison.b.period}</th>
                                                    <th className="text-right">Δ</th>
                                                    <th className="text-right">Tendencia</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {Object.entries(comparison.comparison).map(([k, v]) => (
                                                    <ComparisonRow key={k} k={k} v={v} />
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </>
                )}
            </main>
            <Footer />
            <WhatsAppFab />
        </div>
    );
}

function PeriodHeader({ analysis, color }) {
    return (
        <div className="gf-card-ficha" style={{ borderTop: `2px solid ${color}` }}>
            <div className="micro-label mb-2" style={{ color }}>Ejercicio {analysis.period}</div>
            <div className="serif text-2xl mb-1">{analysis.company_name}</div>
        </div>
    );
}

function ComparisonRow({ k, v }) {
    const isPct = ["roe", "roa", "margen_neto", "endeudamiento", "autonomia_financiera"].includes(k);
    const format = (x) => x === null || x === undefined ? "—" : isPct ? `${formatNum(x * 100, 1)}%` : formatNum(x, 2);
    const d = v.delta;
    // Endeudamiento: menos es mejor. ROE/ROA/margen: más es mejor. Para la flecha, convención: delta >0 verde si es positivo
    const isBad = ["endeudamiento"].includes(k);
    let trendColor = "var(--gf-text-muted)";
    let TrendIcon = Minus;
    if (d !== null && d !== undefined) {
        if (d > 0) {
            trendColor = isBad ? "var(--gf-danger)" : "var(--gf-success)";
            TrendIcon = TrendUp;
        } else if (d < 0) {
            trendColor = isBad ? "var(--gf-success)" : "var(--gf-danger)";
            TrendIcon = TrendDown;
        }
    }
    return (
        <tr>
            <td>{v.label}</td>
            <td className="text-right mono text-sm">{format(v.a)}</td>
            <td className="text-right mono text-sm">{format(v.b)}</td>
            <td className="text-right mono text-sm" style={{ color: trendColor }}>{d !== null && d !== undefined ? (isPct ? `${d > 0 ? "+" : ""}${formatNum(d * 100, 2)}%` : `${d > 0 ? "+" : ""}${formatNum(d, 2)}`) : "—"}</td>
            <td className="text-right">
                <span className="inline-flex items-center gap-1" style={{ color: trendColor }}>
                    <TrendIcon size={14} weight="bold" />
                </span>
            </td>
        </tr>
    );
}
